import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BillingEntityDto,
  CreateBillingEntityDto,
  GetAllBillingEntityDto,
  GetBillingEntitiesDto,
  PatchBillingEntityBoDto,
  PatchBillingEntityDto,
} from "../types/api/api-types";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";

const patchBillingEntity = async (data: PatchBillingEntityDto) => {
  return axiosApi.patch<BillingEntityDto>(`/billing-entity`, data);
};

export const usePatchBillingEntity = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: patchBillingEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["billingEntities"] });
      toast.success(t("billingEntityApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("billingEntityApi.updateError"));
    },
  });
};

const patchBillingEntityBo = async ({
  data,
  id,
}: {
  data: PatchBillingEntityBoDto;
  id: string;
}) => {
  return axiosApi.patch(`/billing-entity/bo/${id}`, data);
};

export const usePatchBillingEntityBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: patchBillingEntityBo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getFullCompanyBO"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyBOV1"] });
      toast.success(t("billingEntityApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("billingEntityApi.updateError"));
    },
  });
};

const deleteBillingEntity = async (id: string) => {
  return axiosApi.delete(`/billing-entity/${id}`);
};

export const useDeleteBillingEntity = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: deleteBillingEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["billingEntities"] });
      toast.success(t("billingEntityApi.deleteSuccess"));
    },
    onError: () => {
      toast.error(t("billingEntityApi.deleteError"));
    },
  });
};

const createBillingEntity = async (data: CreateBillingEntityDto) => {
  return axiosApi.post(`/billing-entity`, data);
};

export const useCreateBillingEntity = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  return useMutation({
    mutationFn: createBillingEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["billingEntities"] });
      toast.success(t("billingEntityApi.createSuccess"));
    },
    onError: () => {
      toast.error(t("billingEntityApi.createError"));
    },
  });
};

const getBillingEntities = async (payload: GetBillingEntitiesDto) => {
  return axiosApi.get<GetAllBillingEntityDto>(`/company/billingEntities`, {
    params: payload,
  });
};

export const useGetBillingEntities = (payload: GetBillingEntitiesDto) => {
  return useQuery({
    queryKey: ["billingEntities", payload],
    queryFn: () => getBillingEntities(payload),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
